
export function restrictNumericInput(event: InputEvent) {
    // Use a regular expression to match only numeric characters
    const numericPattern = /^[0-9]*$/;

    let isInputElement = event.target instanceof HTMLInputElement;

    if (!isInputElement) {
        throw new Error("Event target is not an input element, please only call restrictNumericInput on input elements");
    }

    // Get the input element and its value
    const inputElement: HTMLInputElement = event.target as HTMLInputElement;
    const inputValue = event.data;

    // If the input value contains non-numeric characters, remove them
    if (inputValue !== null && inputValue !== undefined && !numericPattern.test(inputValue)) {
        inputElement.value = inputElement.value.replace(/[^0-9]/g, '');
    }

    let parsedNumber = Number.parseInt(inputElement.value);
    parsedNumber = isNaN(parsedNumber) ? 0 : parsedNumber;

    inputElement.value = parsedNumber.toFixed(0);
}

/**
 * Validates that a text input is not empty and contains both words (for name fields)
 * @param value The input value to validate
 * @returns true if valid, false otherwise
 */
export function validateName(value: string): boolean {
    const trimmedValue = value.trim();
    // Check if value is not empty and contains at least one space (for first and last name)
    return trimmedValue.length > 0 && trimmedValue.includes(' ');
}

/**
 * Validates an email address format
 * @param email The email address to validate
 * @returns true if valid email format, false otherwise
 */
export function validateEmail(email: string): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email.trim());
}

/**
 * Validates that a text input is not empty
 * @param value The input value to validate
 * @returns true if not empty, false otherwise
 */
export function validateNotEmpty(value: string): boolean {
    return value.trim().length > 0;
}

/**
 * Validates that a select element has a value selected (not the default empty option)
 * @param value The select value to validate
 * @returns true if a value is selected, false otherwise
 */
export function validateSelectHasValue(value: string): boolean {
    return value.trim().length > 0;
}

/**
 * Validates that a date input has a value
 * @param value The date input value to validate
 * @returns true if a date is selected, false otherwise
 */
export function validateDate(value: string): boolean {
    return value.trim().length > 0;
}

/**
 * Validates that a number input has a positive value
 * @param value The number input value to validate
 * @returns true if positive number, false otherwise
 */
export function validatePositiveNumber(value: string): boolean {
    const num = Number(value);
    return !isNaN(num) && num > 0;
}

/**
 * Validates that at least one checkbox in a group is checked
 * @param checkboxes Array of checkbox elements to validate
 * @returns true if at least one is checked, false otherwise
 */
export function validateAtLeastOneCheckbox(checkboxes: HTMLInputElement[]): boolean {
    return checkboxes.some(checkbox => checkbox.checked);
}

/**
 * Shows an error message by removing the 'invisible' class
 * @param errorElement The error element to show
 */
export function showError(errorElement: HTMLElement): void {
    errorElement.classList.remove('invisible');
}

/**
 * Hides an error message by adding the 'invisible' class
 * @param errorElement The error element to hide
 */
export function hideError(errorElement: HTMLElement): void {
    errorElement.classList.add('invisible');
}
